import { Skeleton, Stack, Typography } from "@mui/material";
import { createFileRoute, Link } from "@tanstack/react-router";

import type { OrionBreadcrumbItem } from "@orion/OrionComponents/OrionBreadcrumbs";

import { useAccount } from "@/api/zero/accounts";
import { SettingsPageLayout } from "@/features/Settings/SettingsPageLayout";
import { EditAccount } from "@/features/ZeroAdmin/accounts/EditAccount";

export const Route = createFileRoute(
  "/_auth/settings/$siteId/_zeroAdmin/accounts/$accountId",
)({
  component: AccountEditPage,
});

function AccountEditPage() {
  const { siteId, accountId } = Route.useParams();
  const { data: account, isLoading, isError } = useAccount(accountId);

  const breadcrumbs: OrionBreadcrumbItem[] = [
    { label: "Settings" },
    {
      label: "Accounts",
      to: "/settings/$siteId/accounts",
      params: { siteId },
    },
  ];

  if (isLoading) {
    return (
      <SettingsPageLayout breadcrumbs={breadcrumbs} title="Account">
        <Stack sx={{ gap: 4 }}>
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={160} />
        </Stack>
      </SettingsPageLayout>
    );
  }

  if (isError || !account) {
    return (
      <SettingsPageLayout breadcrumbs={breadcrumbs} title="Account not found">
        <Stack sx={{ gap: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: "error.main" }}
            data-testid="account-edit-error"
          >
            Failed to load account.
          </Typography>
          <Link to="/settings/$siteId/accounts" params={{ siteId }}>
            Back to accounts
          </Link>
        </Stack>
      </SettingsPageLayout>
    );
  }

  return <EditAccount key={account.id} siteId={siteId} account={account} />;
}
